"use client";

import { Suspense, useEffect, useState } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { MailX } from "lucide-react";
import { PageDecorLayer } from "@/components/decorative/PageDecorLayer";
import { FormSuccessState } from "@/components/motion";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { api } from "@/lib/api-client";
import { cn } from "@/lib/utils";
import { z } from "zod";

function NewsletterUnsubscribeForm() {
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const { submit, status, error, reset, isLoading } = useFormSubmit(
    (data: { email: string }) => api.unsubscribeNewsletter(data)
  );

  useEffect(() => {
    const fromQuery = searchParams.get("email")?.trim();
    if (fromQuery) {
      setEmail(fromQuery);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    const result = z.string().email().safeParse(email.trim());
    if (!result.success) {
      setEmailError("Enter a valid email address");
      return;
    }

    await submit({ email: result.data });
  };

  return (
    <Card variant="glass">
      <CardBody className="space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <MailX size={20} aria-hidden />
          </div>
          <div>
            <h1 className="font-display text-xl font-bold text-[var(--site-text)]">
              Unsubscribe from newsletter
            </h1>
            <p className="mt-1 text-sm text-[var(--site-muted)]">
              Enter your email address to stop receiving program updates and
              impact stories.
            </p>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {status === "success" ? (
            <FormSuccessState
              key="success"
              message="You have been unsubscribed from our newsletter."
              className="border-[var(--site-border)] bg-[var(--site-surface)]"
            >
              <div className="flex flex-wrap gap-2">
                <Button variant="ghost" size="sm" onClick={reset}>
                  Unsubscribe another email
                </Button>
                <Link
                  href="/"
                  className="inline-flex h-9 items-center justify-center rounded-lg border border-[var(--site-border)] px-3 text-sm font-medium text-[var(--site-text)] transition-colors hover:bg-[var(--site-surface)]"
                >
                  Back to home
                </Link>
              </div>
            </FormSuccessState>
          ) : (
            <motion.form
              key="form"
              onSubmit={handleSubmit}
              className="space-y-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div>
                <Label htmlFor="unsubscribe-email">Email address</Label>
                <Input
                  id="unsubscribe-email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(null);
                  }}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="mt-1"
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? "unsubscribe-email-error" : undefined}
                />
                {emailError ? (
                  <p
                    id="unsubscribe-email-error"
                    className="mt-1 text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {emailError}
                  </p>
                ) : null}
              </div>

              <Button type="submit" variant="primary" disabled={isLoading}>
                {isLoading ? "Unsubscribing…" : "Unsubscribe"}
              </Button>

              <p
                role="status"
                aria-live="polite"
                className={cn(
                  "text-sm",
                  status === "error"
                    ? "text-red-600 dark:text-red-400"
                    : "sr-only"
                )}
              >
                {status === "error" &&
                  (error ?? "Unsubscribe failed. Please try again.")}
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </CardBody>
    </Card>
  );
}

export default function NewsletterUnsubscribePage() {
  return (
    <div className="site-page relative px-4 pb-16 pt-[calc(var(--header-offset)+2rem)]">
      <PageDecorLayer variant="section" palette="site" className="opacity-30" />
      <div className="relative z-10 mx-auto max-w-lg">
        <Suspense
          fallback={
            <Card variant="glass">
              <CardBody>
                <p className="text-sm text-[var(--site-muted)]">Loading…</p>
              </CardBody>
            </Card>
          }
        >
          <NewsletterUnsubscribeForm />
        </Suspense>
      </div>
    </div>
  );
}
